import { useState, useEffect, useRef } from 'react';

import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  List,
  Paper,
  Dialog,
  Divider,
  ListItem,
  Typography,
  IconButton,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  DialogContent,
  ListItemButton,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';

// ----------------------------------------------------------------------

export interface TabConfig {
  id: string;
  label: string;
  icon: string;
  component: React.ReactNode | (() => React.ReactNode);
}

export interface TabbedModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  tabs: TabConfig[];
  defaultTab?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  showMoreOptions?: boolean;
  onMoreOptionsClick?: () => void;
  paperProps?: any;
  headerActions?: React.ReactNode;
  onBeforeClose?: () => boolean;
  onNavigateUp?: () => void;
  onNavigateDown?: () => void;
  /** Allow nested dialogs (e.g. reason prompt) to keep text-field focus. */
  disableEnforceFocus?: boolean;
}

export function TabbedModal({
  open,
  onClose,
  title,
  tabs,
  defaultTab,
  maxWidth = 'md',
  fullWidth = true,
  showMoreOptions = false,
  onMoreOptionsClick,
  paperProps,
  headerActions,
  onBeforeClose,
  onNavigateUp,
  onNavigateDown,
  disableEnforceFocus = false,
}: TabbedModalProps) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || '');
  const previousOpenRef = useRef(open);
  const previousDefaultTabRef = useRef(defaultTab);

  // Update active tab only when modal opens or defaultTab prop changes
  useEffect(() => {
    const wasClosed = previousOpenRef.current === false && open === true;
    const defaultTabChanged = previousDefaultTabRef.current !== defaultTab;

    if (wasClosed || (open && defaultTabChanged)) {
      if (defaultTab && tabs.some((tab) => tab.id === defaultTab)) {
        setActiveTab(defaultTab);
      } else if (tabs[0]?.id) {
        setActiveTab(tabs[0].id);
      }
    }

    previousOpenRef.current = open;
    previousDefaultTabRef.current = defaultTab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTab]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const handleClose = () => {
    if (onBeforeClose && !onBeforeClose()) {
      return;
    }
    onClose();
  };

  const activeTabConfig = tabs.find((tab) => tab.id === activeTab);

  // Render the active tab component (support both direct components and render functions)
  const renderActiveTabContent = () => {
    if (!activeTabConfig) return null;
    const { component } = activeTabConfig;
    return typeof component === 'function' ? component() : component;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEnforceFocus={disableEnforceFocus}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '90%',
        },
        ...paperProps,
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          padding: '8px 8px 10px 16px',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          {(onNavigateUp || onNavigateDown) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1, flexShrink: 0, pt: 0.25 }}>
              <IconButton
                size="small"
                onClick={onNavigateUp}
                disabled={!onNavigateUp}
                sx={{ p: 0.25 }}
              >
                <Iconify icon="eva:arrow-ios-upward-fill" width={18} height={18} />
              </IconButton>
              <IconButton
                size="small"
                onClick={onNavigateDown}
                disabled={!onNavigateDown}
                sx={{ p: 0.25 }}
              >
                <Iconify icon="eva:arrow-ios-downward-fill" width={18} height={18} />
              </IconButton>
            </Box>
          )}
          <Typography
            variant="h6"
            component="div"
            sx={{
              flex: 1,
              minWidth: 0,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
              maxHeight: '6.5em',
              overflowY: 'auto',
            }}
            title={title}
          >
            {title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, pt: 0.25 }}>
          {headerActions}
          {showMoreOptions && (
            <IconButton size="small" onClick={onMoreOptionsClick} sx={{ mr: 0.5 }}>
              <Iconify icon="eva:more-horizontal-fill" width={20} height={20} />
            </IconButton>
          )}
          <IconButton size="small" onClick={handleClose}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', minHeight: 400 }}>
        {/* Left Navigation */}
        <Paper
          sx={{
            width: '100px',
            borderRight: `1px solid ${theme.palette.divider}`,
            borderRadius: 0,
            py: 0.5,
          }}
        >
          <List sx={{ py: 0 }}>
            {tabs.map((tab) => {
              const isSelected = activeTab === tab.id;
              return (
                <Box
                  key={tab.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: 1,
                  }}
                >
                  <ListItem disablePadding sx={{ justifyContent: 'center', alignItems: 'center' }}>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => handleTabChange(tab.id)}
                      disableRipple
                      disableTouchRipple
                      sx={{
                        padding: 0,
                        bgcolor: 'transparent !important',
                        '&.Mui-selected': {
                          bgcolor: 'transparent',
                        },
                        '&:hover': {
                          bgcolor: 'transparent',
                        },
                        '&:active': {
                          bgcolor: 'transparent',
                          transform: 'none',
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          minHeight: 40,
                          color: isSelected ? theme.palette.secondary.main : 'text.secondary',
                          margin: 0,
                          justifyContent: 'center',
                          alignItems: 'center',
                          backgroundColor: isSelected
                            ? alpha(theme.palette.secondary.main, 0.08)
                            : 'transparent',
                          borderRadius: 1,
                          transition: 'background-color 0.2s',
                          '.MuiListItemButton-root:hover &': {
                            backgroundColor: isSelected
                              ? alpha(theme.palette.secondary.main, 0.12)
                              : alpha(theme.palette.text.secondary, 0.08),
                          },
                        }}
                      >
                        <Iconify icon={tab.icon} width={24} height={24} />
                      </ListItemIcon>
                      <ListItemText
                        primary={tab.label}
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontSize: '11px',
                          fontWeight: 400,
                          color: isSelected ? theme.palette.secondary.main : 'text.primary',
                          textAlign: 'center',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </Box>
              );
            })}
          </List>
        </Paper>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {renderActiveTabContent()}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
